import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import { Question } from '@app/forms/subscription/definition/interface';
import { environment } from '@environments/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { mergeMap, Observable, tap } from 'rxjs';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { completedQuestions, steps, updateSvgPath } from '../utils';

interface CategorizedData {
  [key: string]: {
    data: Question[];
    previousStepName: string;
    previous?: boolean;
    isComplete: boolean;
    previousStepCompleted: boolean;
    allPreviousStepsCompleted?: boolean;
  };
}

@UntilDestroy()
@Component({
  selector: 'ea-subscription-info',
  templateUrl: './ea-subscription-info.component.html',
  styleUrls: ['./ea-subscription-info.component.scss'],
  standalone: false,
})
export class EaSubscriptionInfoComponent implements OnInit {
  @ViewChildren('selectStep') selectStep: QueryList<ElementRef>;

  pictureFile: string = '';
  consultantBackUrl = environment.servers.resources.url || '';
  toggle: boolean = false;
  stepIndex;
  questions: Question[] = [];
  step;
  previoussStep;
  previousCategory: string = '';
  questionsByCategory;
  stepChanged$: Observable<any>;

  steps = steps;
  category: string;
  completedQuestions = completedQuestions;
  stepIconCompleted = {
    ...this.completedQuestions,
  };
  groupedData;
  categorizedData: CategorizedData = {};

  constructor(
    public subscriptionFacade: SubscriptionFacadeService,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.subscribeToStepChanges();
  }

  private subscribeToStepChanges() {
    this.stepChanged$ = this.subscriptionFacade.stepChanged$.pipe(
      untilDestroyed(this),
      mergeMap(step => {
        this.step = step;
        if (!this.previoussStep) this.previoussStep = this.step;
        return this.subscriptionFacade.questionsChanged$;
      }),
      tap(questions => {
        if (questions && this.step) {
          this.initializeQuestions();
        }
      }),
    );
  }

  private initializeQuestions() {
    this.questions = this.subscriptionFacade.getQuestions();
    this.steps = [...new Set(this.questions.map(q => q.category))];
    const question = this.questions[this.step - 1];
    this.category = question?.category;
    if (!this.previousCategory) this.previousCategory = this.category;
    this.updateCompletedQuestions(this.category);
    this.setStepIndex(question);
  }
  private getQuestionTitle(question: Question): string {
    return !_.isNil(question.getTitle)
      ? question.getTitle(
          this.subscriptionFacade.getPledge(),
          this.subscriptionFacade.getUser(),
        )
      : question.title;
  }
  private groupDataByCategory(): void {
    this.groupedData = _.groupBy(this.questions, 'category');
    _.forEach(this.groupedData, questions =>
      _.remove(
        questions,
        (question: Question) => !this.getQuestionTitle(question),
      ),
    );
    if (!this.steps?.length) {
      return;
    }
    this.steps?.forEach((category, index) => {
      const isCurrentCategoryComplete = this.isComplete(category);
      const allPreviousStepsCompleted =
        this.areAllPreviousStepsCompleted(index);

      this.categorizedData[category] = {
        data: this.groupedData[category],
        previousStepName: this.getPreviousStepName(index),
        isComplete: isCurrentCategoryComplete,
        previous: this.steps.indexOf(this.category) >= index,
        previousStepCompleted: this.isPreviousStepCompleted(index),
        allPreviousStepsCompleted,
      };
    });
  }
  private areAllPreviousStepsCompleted(index: number): boolean {
    if (index === 0) {
      return true;
    }

    return this.steps
      .slice(0, index)
      .every(prevCategory => this.categorizedData[prevCategory]?.isComplete);
  }
  private getPreviousStepName(index: number): string | null {
    return index === 0 ? null : this.steps[index - 1];
  }
  private isPreviousStepCompleted(index: number): boolean {
    return index === 0
      ? true
      : this.categorizedData?.[this?.steps?.[index - 1]]?.isComplete || false;
  }

  private setStepIndex(question: Question) {
    if (this.category) {
      this.questionsByCategory = this.questions.filter(
        question => question.category === this.category,
      );
      this.stepIndex = this.getIndexOfIdInNestedArray(
        this.chunkArray(this.questionsByCategory, 4),
        question.id,
      );
    }
  }

  ngOnDestroy(): void {}
  isComplete(category) {
    const pledge = this.subscriptionFacade.getPledge();
    const questionsByCategory = this.questions.filter(
      q => q.category === category,
    );

    const questionsByCategoryAndCompleted = questionsByCategory.filter(
      q =>
        (q.callbackValidation &&
          q.callbackValidation(pledge, this.subscriptionFacade.getUser())) ||
        !q.callbackValidation,
    );

    const lengthCompletes = questionsByCategory.length;
    const lengthCompletesByCategory = questionsByCategoryAndCompleted.length;

    return lengthCompletes && lengthCompletesByCategory == lengthCompletes;
  }
  updateCompletedQuestions(currentCategory) {
    this.steps.forEach((category, index) => {
      const indexOfCurrentCategory = this.steps.indexOf(currentCategory);
      if (!this.completedQuestions[currentCategory]) {
        this.completedQuestions[currentCategory] =
          this.isComplete(currentCategory);
      }
      if (indexOfCurrentCategory && indexOfCurrentCategory > index) {
        if (this.completedQuestions[category]) {
          this.completedQuestions[category] = true;
        } else {
          const completed = this.isComplete(category);
          this.completedQuestions[category] = completed;
        }
      }
    });
  }

  previous(index) {
    return (
      (this.steps.indexOf(this.previousCategory) ||
        this.steps.indexOf(this.category)) >= index
    );
  }
  next(index) {
    const key = this.steps[index + 1];
    if (!key) return false;

    return this.completedQuestions[key];
  }
  chunkArray(arr, n) {
    const chunkLength = Math.max(arr.length / n, 1);
    const chunks = [];
    for (let i = 0; i < n; i++) {
      if (chunkLength * (i + 1) <= arr.length)
        chunks.push(arr.slice(chunkLength * i, chunkLength * (i + 1)));
    }

    return chunks;
  }
  getIndexOfIdInNestedArray(arr, id) {
    for (let i = 0; i < arr.length; i++) {
      let index = arr[i].findIndex(k => k.id == id);
      if (index > -1) {
        return i + 1;
      }
    }
  }
  getQuestionTotal(category) {
    return this.questions?.filter(q => q.category === category)?.length;
  }
  setStep(step, i) {
    if (i !== 0) {
      const element =
        this.selectStep.toArray()[i - 1]?.nativeElement?.children[0];

      if (!element?.querySelector('.green-svg')) {
        return;
      }
    }
    const q = this.questions?.findIndex(q => q.category === step);

    if ((q || q == 0) && q !== -1) {
      this.subscriptionFacade.setStep(Number(q) + 1);
    }
  }

  private setSvgPath(step, i: any) {
    updateSvgPath(
      step,
      i,
      this.selectStep,
      this.completedQuestions,
      this.category,
      this.renderer,
      this.stepIconCompleted,
    );
  }

  private isStepCompeteWithoutLastStep(step) {
    return this.completedQuestions[step] && step !== 'Signature';
  }
  isCompletedQuestions(step, i) {
    this.setSvgPath(step, i);
    return step == this.category || this.isStepCompeteWithoutLastStep(step);
  }
  getClassSteperDash(step, index, i) {
    return (this.completedQuestions[step] &&
      this.isCompletedQuestions(step, index)) ||
      (step == this.category &&
        this.questionsByCategory?.length == 1 &&
        this.completedQuestions[step]) ||
      (step == this.category && i <= this.stepIndex) ||
      this.next(index)
      ? 'green-svg-number'
      : 'empty-svg-number';
  }
}
